import { api } from '@/utils/api';

/** 'Go to' = the portal's pages and actions (utils/featureSearch.ts), matched without the API. */
export type SearchGroup = 'Go to' | 'Campaigns' | 'Creators' | 'Invoices';

export interface SearchResult {
  id: string;
  group: SearchGroup;
  title: string;
  subtitle: string;
  icon: string;
  path: string;
}

interface SearchResponse {
  campaigns: { id: string; name: string; status: string; markets: string[] }[];
  /** On the agency API: a roster row, with the campaign it belongs to. */
  creators: { id: string; name: string; handle: string; city: string; campaignId?: string; campaignName?: string }[];
  invoices: { id: string; number: string; creatorName: string; campaign: string; amount: string }[];
}

const join = (...parts: string[]) => parts.filter(Boolean).join(' · ');

/** GET /search — each group is limited to what the signed-in role can open. */
export async function searchPortal(rawQuery: string, signal?: AbortSignal): Promise<SearchResult[]> {
  const query = rawQuery.trim();
  if (query.length < 2) return [];
  const found = await api<SearchResponse>(`/search?q=${encodeURIComponent(query)}`, { signal });
  return [
    ...found.campaigns.map((campaign) => ({
      id: `campaign-${campaign.id}`,
      group: 'Campaigns' as const,
      title: campaign.name,
      // No brand/client column in production, so the markets stand in for it.
      subtitle: join(campaign.markets.join(', '), campaign.status),
      icon: 'ri-megaphone-line',
      path: `/campaigns/${campaign.id}`,
    })),
    ...found.creators.map((creator) => ({
      id: `creator-${creator.id}`,
      group: 'Creators' as const,
      title: creator.name,
      subtitle: creator.campaignId ? join(creator.handle, creator.campaignName ?? '') : join(creator.handle, creator.city),
      icon: 'ri-user-star-line',
      path: creator.campaignId ? `/campaigns/${creator.campaignId}?tab=creators` : `/creators/${creator.id}`,
    })),
    ...found.invoices.map((invoice) => ({
      id: `invoice-${invoice.id}`,
      group: 'Invoices' as const,
      title: `${invoice.number} — ${invoice.creatorName}`,
      subtitle: join(invoice.campaign, invoice.amount),
      icon: 'ri-file-list-3-line',
      path: `/invoices/${invoice.id}`,
    })),
  ];
}
